import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import SearchIcon from "@mui/icons-material/Search";
import SendIcon from "@mui/icons-material/Send";
import {
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Grid,
  InputAdornment,
  MenuItem,
  Tab,
  Tabs,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiService from "../../../service/Api";
import { tokens } from "../../../theme";
import { IColorTokens } from "../../../types/theme";
import { usePermissions } from "../../../hooks/usePermissions";
import { PERMISSIONS } from "../../../constants/permissions";

interface ApiEmailTemplate {
  id: number;
  name: string;
  description: string;
  category: string;
  status: "draft" | "published";
  version: string;
  sent_count: number;
  updated_at: string;
}

interface EmailTemplate {
  id: string;
  title: string;
  description: string;
  status: "Published" | "Draft";
  version: string;
  sentCount: number;
  timeAgo: string;
  category: string;
}

// Mirrors EmailTemplate.CATEGORIES in backend/configuration/models.py --
// dev-managed choices, kept in sync by hand rather than fetched.
const CATEGORY_OPTIONS: { value: string; label: string }[] = [
  { value: "system", label: "System" },
  { value: "live_streaming", label: "Live Streaming" },
  { value: "pixel_pilot", label: "Pixel Pilot" },
  { value: "muzukuru", label: "Muzukuru" },
  { value: "chema", label: "Chema" },
  { value: "my_muzukuru", label: "MyMuzukuru" },
];

const categories = ["All Emails", ...CATEGORY_OPTIONS.map((c) => c.label)];

const mapTemplate = (t: ApiEmailTemplate): EmailTemplate => ({
  id: String(t.id),
  title: t.name,
  description: t.description,
  status: t.status === "published" ? "Published" : "Draft",
  version: t.version,
  sentCount: t.sent_count,
  timeAgo: formatDistanceToNow(new Date(t.updated_at), { addSuffix: true }),
  category:
    CATEGORY_OPTIONS.find((c) => c.value === t.category)?.label ?? t.category,
});

export default function EmailTemplatesManager() {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("All");
  const [templates, setTemplates] = useState<EmailTemplate[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    apiService
      .get<{ results: ApiEmailTemplate[] } | ApiEmailTemplate[]>(
        "/api/configurations/email-templates/?page_size=100",
      )
      .then((res) => {
        const data = Array.isArray(res.data) ? res.data : res.data.results ?? [];
        setTemplates(data.map(mapTemplate));
      })
      .catch(() => setTemplates([]))
      .finally(() => setLoading(false));
  }, []);

  const activeCategory = categories[activeTab];

  const filteredTemplates = templates.filter((t) => {
    const matchesCategory =
      activeCategory === "All Emails" || t.category === activeCategory;
    const matchesSearch =
      search.trim() === "" ||
      t.title.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = status === "All" || t.status === status;
    return matchesCategory && matchesSearch && matchesStatus;
  });

  return (
    <Box
      sx={{
        p: 5,
        bgcolor: colors.primary[900],
        minHeight: "100vh",
        borderRadius: 5,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 700,
            color: colors.primary[700],
            fontSize: "1.75rem",
          }}
        >
          Email Templates Manager
        </Typography>
        {hasPermission(PERMISSIONS.CONFIGURATION_ADD) && (
          <Button
            variant="contained"
            onClick={() => navigate("/configurations/email-templates/add")}
            sx={{
              bgcolor: colors.primary[400],
              "&:hover": { bgcolor: colors.primary[500] },
              borderRadius: 2,
              textTransform: "none",
              fontSize: "0.9rem",
              px: 3,
            }}
          >
            Create Email
          </Button>
        )}
      </Box>

      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        sx={{
          mb: 3,
          "& .MuiTabs-indicator": { bgcolor: colors.primary[400] },
          "& .Mui-selected": {
            color: `${colors.primary[400]} !important`,
            fontWeight: "bold",
          },
        }}
      >
        {categories.map((cat) => (
          <Tab
            key={cat}
            label={cat}
            sx={{
              textTransform: "none",
              color: colors.primary[300],
              fontSize: "0.875rem",
            }}
          />
        ))}
      </Tabs>

      <Box sx={{ display: "flex", gap: 2, mb: 4 }}>
        <TextField
          placeholder="Search here"
          size="small"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          sx={{
            bgcolor: colors.primary[900],
            borderRadius: 8,
            width: 280,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            ),
          }}
        />
        <TextField
          select
          size="small"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          sx={{
            bgcolor: colors.primary[900],
            borderRadius: 8,
            width: 120,
            "& .MuiOutlinedInput-root": {
              borderRadius: "8px",
            },
          }}
        >
          <MenuItem value="All">Status</MenuItem>
          <MenuItem value="Published">Published</MenuItem>
          <MenuItem value="Draft">Draft</MenuItem>
        </TextField>
      </Box>

      <Grid container spacing={3}>
        {loading ? (
          <Grid size={{ xs: 12 }}>
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress sx={{ color: colors.primary[400] }} />
            </Box>
          </Grid>
        ) : filteredTemplates.length === 0 ? (
          <Grid size={{ xs: 12 }}>
            <Typography
              sx={{ color: colors.primary[300], textAlign: "center", py: 6 }}
            >
              No templates found for &quot;{activeCategory}&quot;
            </Typography>
          </Grid>
        ) : (
          filteredTemplates.map((template) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={template.id}>
              <TemplateCard
                onClick={() =>
                  navigate(
                    `/configurations/email-templates/view/${template.id}`,
                  )
                }
                template={template}
                colors={colors}
              />
            </Grid>
          ))
        )}
      </Grid>
    </Box>
  );
}

function TemplateCard({
  onClick,
  template,
  colors,
}: {
  onClick: () => void;
  template: EmailTemplate;
  colors: IColorTokens;
}) {
  const isPublished = template.status === "Published";

  return (
    <Card
      onClick={onClick}
      sx={{
        p: 2.5,
        borderRadius: "24px",
        border: `2px solid ${colors.primary[800]}`,
        backgroundColor: colors.primary[900],
        boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.02)",
        cursor: "pointer",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "scale(1.02)",
          boxShadow: "0px 6px 24px rgba(0, 0, 0, 0.05)",
        },
      }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: "1rem",
          color: colors.primary[700],
          mb: 0.5,
        }}
      >
        {template.title}
      </Typography>
      <Typography variant="body2" sx={{ color: colors.primary[100], mb: 2 }}>
        {template.description}
      </Typography>

      <Box sx={{ display: "flex", gap: 1, mb: 3 }}>
        <Chip
          label={template.category}
          size="small"
          sx={{
            bgcolor: colors.primary[800],
            color: colors.primary[300],
            fontWeight: 500,
            height: 24,
          }}
        />
        <Chip
          label={template.status}
          size="small"
          sx={{
            bgcolor: isPublished ? "#EAFCF4" : "#FFF0F0",
            color: isPublished ? "#25C483" : "#FF6B6B",
            fontWeight: 500,
            height: 24,
          }}
        />
      </Box>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          color: colors.primary[300],
          pt: 1,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccountTreeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{template.version}</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <SendIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{template.sentCount} sent</Typography>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <AccessTimeIcon sx={{ fontSize: 14 }} />
          <Typography variant="caption">{template.timeAgo}</Typography>
        </Box>
      </Box>
    </Card>
  );
}
